import { useState, useRef, useEffect } from "react";
import axios from "axios";
import { API } from "../App";
import {
  PenLine, X, Send, Loader2, Trash2, Download, FileText,
  ChevronDown, ChevronUp, Sparkles
} from "lucide-react";

const AiNoteTaker = ({ context, contextLabel }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [notes, setNotes] = useState([]);
  const [input, setInput] = useState("");
  const [aiSummary, setAiSummary] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const notesEndRef = useRef(null);

  useEffect(() => {
    notesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [notes]);

  const addNote = () => {
    if (!input.trim()) return;
    setNotes((prev) => [
      ...prev,
      { text: input, timestamp: new Date().toISOString(), type: "manual" },
    ]);
    setInput("");
  };

  const deleteNote = (idx) => {
    setNotes((prev) => prev.filter((_, i) => i !== idx));
  };

  const summarizeWithAi = async () => {
    if (notes.length === 0) return;
    setLoading(true);
    try {
      const noteTexts = notes.map((n) => `- ${n.text}`).join("\n");
      const prompt = `Summarize and organize these ${contextLabel || "QMS"} notes into a structured format suitable for a formal report. Group related items and add any recommended actions:\n\n${noteTexts}`;

      const res = await axios.post(
        `${API}/ai/chat`,
        { message: prompt, session_id: sessionId, context: context },
        { withCredentials: true }
      );
      setSessionId(res.data.session_id);
      setAiSummary(res.data.response);
    } catch (err) {
      setAiSummary("Failed to generate summary. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const exportNotes = (format) => {
    const title = `${contextLabel || "QMS"} Notes - ${new Date().toLocaleDateString()}`;
    let content = `# ${title}\n\n`;

    if (notes.length > 0) {
      content += "## Notes\n\n";
      notes.forEach((n, i) => {
        const time = new Date(n.timestamp).toLocaleTimeString();
        content += `${i + 1}. [${time}] ${n.text}\n`;
      });
    }

    if (aiSummary) {
      content += `\n## AI Summary\n\n${aiSummary}\n`;
    }

    const blob = new Blob([content], { type: "text/markdown" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${(contextLabel || "notes").replace(/\s+/g, "_").toLowerCase()}_${new Date().toISOString().slice(0, 10)}.md`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (!isOpen) {
    return (
      <button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-6 right-20 w-10 h-10 bg-[#E8A838] hover:bg-[#FBBF24] text-[#111318] rounded-full flex items-center justify-center shadow-lg transition-all z-50 hover:scale-105"
        data-testid="notetaker-open-btn"
        title="AI Note Taker"
      >
        <PenLine size={18} />
      </button>
    );
  }

  return (
    <div
      className={`fixed right-4 z-50 bg-[#181B23] border border-[rgba(255,255,255,0.08)] rounded-[3px] shadow-2xl flex flex-col transition-all ${
        isMinimized ? "bottom-4 w-[320px] h-12" : "bottom-4 w-[360px] h-[520px]"
      }`}
      data-testid="notetaker-panel"
    >
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2.5 border-b border-[rgba(255,255,255,0.06)] flex-shrink-0">
        <div className="flex items-center gap-2">
          <PenLine size={14} className="text-[#E8A838]" />
          <span className="font-['Syne'] font-semibold text-white text-sm">Notes</span>
          {contextLabel && (
            <span className="text-[10px] text-[#8B95A5] bg-[#1F222D] px-2 py-0.5 rounded-full">{contextLabel}</span>
          )}
          {notes.length > 0 && (
            <span className="text-[10px] text-[#E8A838] bg-[rgba(232,168,56,0.1)] px-1.5 py-0.5 rounded-full">
              {notes.length}
            </span>
          )}
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={() => setIsMinimized(!isMinimized)}
            className="text-[#8B95A5] hover:text-white p-1"
          >
            {isMinimized ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
          </button>
          <button
            onClick={() => setIsOpen(false)}
            className="text-[#8B95A5] hover:text-white p-1"
            data-testid="notetaker-close-btn"
          >
            <X size={14} />
          </button>
        </div>
      </div>

      {!isMinimized && (
        <>
          {/* Notes List */}
          <div className="flex-1 overflow-y-auto px-3 py-2 space-y-2">
            {notes.length === 0 && (
              <div className="text-center py-8 text-[#8B95A5]">
                <PenLine size={24} className="mx-auto mb-2 opacity-30" />
                <p className="text-xs">Start taking notes for this {contextLabel || "module"}</p>
              </div>
            )}
            {notes.map((note, i) => (
              <div
                key={i}
                className="group bg-[#1F222D] rounded-[3px] p-2.5 border border-[rgba(255,255,255,0.04)]"
              >
                <div className="flex items-start justify-between gap-2">
                  <p className="text-[#CBD5E1] text-xs leading-relaxed flex-1">{note.text}</p>
                  <button
                    onClick={() => deleteNote(i)}
                    className="text-[#2D313D] group-hover:text-[#F87171] transition-colors flex-shrink-0"
                  >
                    <Trash2 size={12} />
                  </button>
                </div>
                <span className="text-[#6B7280] text-[10px] mt-1 block">
                  {new Date(note.timestamp).toLocaleTimeString()}
                </span>
              </div>
            ))}
            <div ref={notesEndRef} />

            {/* AI Summary */}
            {aiSummary && (
              <div className="bg-[rgba(54,169,225,0.05)] border border-[rgba(54,169,225,0.15)] rounded-[3px] p-3 mt-2">
                <div className="flex items-center gap-1.5 mb-2">
                  <Sparkles size={12} className="text-[#36A9E1]" />
                  <span className="text-[#36A9E1] text-[10px] font-medium uppercase tracking-wider">AI Summary</span>
                </div>
                <p className="text-[#CBD5E1] text-xs leading-relaxed whitespace-pre-wrap">{aiSummary}</p>
              </div>
            )}
          </div>

          {/* Actions */}
          {notes.length > 0 && (
            <div className="flex items-center gap-1.5 px-3 py-2 border-t border-[rgba(255,255,255,0.04)]">
              <button
                onClick={summarizeWithAi}
                disabled={loading}
                className="flex items-center gap-1 px-2 py-1 text-[10px] text-[#36A9E1] bg-[rgba(54,169,225,0.08)] hover:bg-[rgba(54,169,225,0.15)] rounded-[3px] transition-colors"
                data-testid="notetaker-summarize-btn"
              >
                {loading ? <Loader2 size={10} className="animate-spin" /> : <Sparkles size={10} />}
                Summarize
              </button>
              <button
                onClick={() => exportNotes("md")}
                className="flex items-center gap-1 px-2 py-1 text-[10px] text-[#E8A838] bg-[rgba(232,168,56,0.08)] hover:bg-[rgba(232,168,56,0.15)] rounded-[3px] transition-colors"
                data-testid="notetaker-export-btn"
              >
                <Download size={10} /> Export
              </button>
            </div>
          )}

          {/* Input */}
          <div className="px-3 py-2 border-t border-[rgba(255,255,255,0.06)]">
            <form
              onSubmit={(e) => {
                e.preventDefault();
                addNote();
              }}
              className="flex gap-2"
            >
              <input
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Type a note..."
                className="flex-1 bg-[#1F222D] border border-[rgba(255,255,255,0.06)] rounded-[3px] px-3 py-1.5 text-[#CBD5E1] text-xs focus:outline-none focus:border-[#E8A838]"
                data-testid="notetaker-input"
              />
              <button
                type="submit"
                disabled={!input.trim()}
                className="bg-[#E8A838] text-[#111318] px-2 py-1.5 rounded-[3px] disabled:opacity-30 hover:bg-[#FBBF24] transition-colors"
                data-testid="notetaker-add-btn"
              >
                <Send size={12} />
              </button>
            </form>
          </div>
        </>
      )}
    </div>
  );
};

export default AiNoteTaker;
